import { makeAutoObservable, runInAction } from 'mobx';
import { api } from '@/api';
import { ApiError } from '@/api/apiClient';
import { humanizeJoinError } from '@/api/errors';
import { on, invoke, setOnReconnected } from '@/api/signalr';
import type { RoomDto, PlayerDto } from '@/api/dto';
import type { RootStore } from './RootStore';
import { fx } from '@/platform/feedback';

type RoomSnapshot = {
  room: RoomDto;
  round: unknown | null;
  match: unknown | null;
  results: unknown | null;
};

export class RoomStore {
  room: RoomDto | null = null;
  isLoading = false;
  error: string | null = null;
  private unsubscribes: Array<() => void> = [];
  // Incremented by unsubscribe() to invalidate in-flight subscribe() calls
  private subscribeGen = 0;
  private roundEndWatchdog: ReturnType<typeof setInterval> | null = null;
  // Coalesces concurrent join attempts for the same code into a single request (N5).
  private joinInFlight: { code: string; promise: Promise<RoomDto> } | null = null;
  // Guards against a double-fire of setReady (e.g. Telegram MainButton and the in-page
  // "Начать" button both wired to the same handler and tapped near-simultaneously) sending
  // two concurrent POST /ready — each of which used to trigger its own TryStartGameAsync
  // and could start two separate matches for the same room (same race class as N7 — B06).
  private isSettingReady = false;

  constructor(private readonly root: RootStore) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    makeAutoObservable(this, { subscribeGen: false } as any, { autoBind: true });
  }

  get code(): string | null {
    return this.room?.code ?? null;
  }

  get me(): PlayerDto | null {
    const pid = this.root.auth.playerId;
    return this.room?.players.find((p) => p.playerId === pid) ?? null;
  }

  get opponents(): PlayerDto[] {
    const pid = this.root.auth.playerId;
    return this.room?.players.filter((p) => p.playerId !== pid) ?? [];
  }

  get canStart(): boolean {
    if (!this.room) return false;
    const minPlayers = this.room.settings.mode === 'Solo' ? 1 : 2;
    return (
      this.room.players.length >= minPlayers &&
      this.room.players.every((p) => p.isReady && p.isConnected)
    );
  }

  async load(code: string): Promise<void> {
    this.isLoading = true;
    this.error = null;
    try {
      const room = await api.rooms.get(code);
      runInAction(() => {
        this.room = room;
        this.isLoading = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isLoading = false;
        this.error = e instanceof Error ? e.message : 'Ошибка загрузки комнаты';
      });
    }
  }

  async setReady(ready: boolean): Promise<void> {
    if (!this.code || this.isSettingReady) return;
    this.isSettingReady = true;
    const code = this.code;
    try {
      const room = await api.rooms.setReady(code, ready);
      runInAction(() => {
        // SignalR may have already advanced the status (RoundStarted race); keep it
        const current = this.room?.status;
        const advanced = current === 'InGame' || current === 'BetweenRounds' || current === 'Finished';
        this.room = advanced ? { ...room, status: current! } : room;
      });
      // Fallback: if game started but RoundStarted was missed, resync from hub
      if (ready && this.room?.status === 'WaitingForPlayers') {
        try {
          const snap = await invoke<RoomSnapshot>('SubscribeRoom', code);
          runInAction(() => {
            if (this.room?.status === 'WaitingForPlayers') this.applySnapshot(snap);
          });
        } catch {
          // non-fatal; UI stays on lobby, SignalR event may still arrive
        }
      }
    } finally {
      runInAction(() => { this.isSettingReady = false; });
    }
  }

  async leave(): Promise<void> {
    if (!this.code) return;
    await api.rooms.leave(this.code);
    runInAction(() => {
      this.room = null;
    });
  }

  applyServerSnapshot(room: RoomDto): void {
    this.room = room;
  }

  // Returns the generation id for this subscription attempt — the caller must
  // pass it back to unsubscribe() so a stale cleanup (React route/StrictMode
  // effect churn can fire several mount→cleanup→mount cycles for the same
  // room) can recognize it's been superseded and skip tearing down a newer,
  // already-active subscription's timers and event handlers.
  subscribe(code: string): number {
    const gen = ++this.subscribeGen;
    this.error = null;
    void this.doSubscribe(code, gen);
    return gen;
  }

  // React route/StrictMode effect churn can call subscribe() more than once for the
  // same room within the same tick (mount→cleanup→mount, or overlapping AnimatePresence
  // layers both resolving the same route) — each call's doSubscribe() would otherwise
  // fire its own POST /join before any of them observes the others' in-flight request.
  // Sharing one promise per code collapses those into a single network call (N5).
  private ensureJoined(code: string): Promise<RoomDto> {
    if (this.joinInFlight?.code === code) return this.joinInFlight.promise;
    const promise = api.rooms.join(code).finally(() => {
      if (this.joinInFlight?.promise === promise) this.joinInFlight = null;
    });
    this.joinInFlight = { code, promise };
    return promise;
  }

  private async doSubscribe(code: string, gen: number): Promise<void> {
    // Ensures this player is actually a room member before subscribing —
    // SubscribeRoom only attaches the live connection, it never adds a
    // player to room.Players. Without this, an already-authenticated
    // player opening a room link directly (not via /join) would render a
    // full lobby while being invisible to everyone else. join() is
    // idempotent for existing members (reconnect branch keyed by
    // externalId/externalType), so calling it here is safe on every mount.
    try {
      await this.ensureJoined(code);
    } catch (e) {
      if (gen !== this.subscribeGen) return;
      runInAction(() => {
        this.error = e instanceof ApiError ? humanizeJoinError(e) : 'Не удалось подключиться к комнате';
      });
      return;
    }
    if (gen !== this.subscribeGen) return;

    try {
      setOnReconnected(async () => {
        const fresh = await invoke<RoomSnapshot>('SubscribeRoom', code);
        runInAction(() => this.applySnapshot(fresh));
        if (fresh.round) void this.root.round.loadProgress(code).catch(() => {});
      });

      const snap = await invoke<RoomSnapshot>('SubscribeRoom', code);

      // If unsubscribe() was called while we were awaiting, bail out
      if (gen !== this.subscribeGen) return;

      runInAction(() => this.applySnapshot(snap));
      // Snapshot itself carries no opponent progress (Q03) — backfill it via REST
      // right after (re)subscribing, instead of waiting for the next live push.
      if (snap.round) void this.root.round.loadProgress(code).catch(() => {});
      this.root.round.startClockSync();
      this.startRoundEndWatchdog(code);

      this.unsubscribes.push(
        on<RoomDto>('RoomUpdated', (room) => runInAction(() => this.applyServerSnapshot(room))),
        on<{ player: PlayerDto }>('PlayerJoined', ({ player }) => runInAction(() => this.upsertPlayer(player))),
        on<{ playerId: string }>('PlayerLeft', ({ playerId }) => runInAction(() => this.removePlayer(playerId))),
        on<{ playerId: string; isReady: boolean }>('PlayerReadyChanged', ({ playerId, isReady }) =>
          runInAction(() => this.setPlayerReady(playerId, isReady)),
        ),
        on<{ playerId: string; isConnected: boolean }>('PlayerConnectionChanged', ({ playerId, isConnected }) =>
          runInAction(() => this.setPlayerConnected(playerId, isConnected)),
        ),
        on<{ round: unknown }>('RoundStarted', ({ round }) => {
          runInAction(() => {
            this.root.round.applyServerRound(round);
            this.root.round.justStarted = true;
            if (this.room) this.room = { ...this.room, status: 'InGame' };
          });
          fx.notify();
        }),
        on<unknown>('OpponentProgress', (p) => runInAction(() => this.root.round.applyProgress(p))),
        on<{ results: unknown }>('RoundEnded', ({ results }) => {
          runInAction(() => {
            this.root.match.applyResults(results);
            if (this.room) this.room = { ...this.room, status: 'BetweenRounds' };
          });
        }),
        on<{ nextRoundStartsAtUtc: string }>('BetweenRoundsStarted', ({ nextRoundStartsAtUtc }) =>
          runInAction(() => this.root.match.applyBetweenRounds(nextRoundStartsAtUtc)),
        ),
        on<{ match: unknown }>('MatchEnded', ({ match }) => {
          runInAction(() => {
            this.root.match.applyMatchEnded(match);
            if (this.room) this.room = { ...this.room, status: 'Finished' };
          });
          void this.root.match.loadMatchInfo(code);
        }),
      );
    } catch (e) {
      console.error('subscribe failed', e);
    }
  }

  // gen must be the value returned by the subscribe() call this cleanup corresponds to.
  // If a newer subscribe() has since become active, this is a stale cleanup left over
  // from route/StrictMode effect churn — skip teardown entirely so it doesn't kill the
  // newer subscription's clock sync, watchdog, or live event handlers.
  unsubscribe(gen: number): void {
    if (gen !== this.subscribeGen) return;
    this.subscribeGen++;
    this.root.round.stopClockSync();
    this.stopRoundEndWatchdog();
    this.unsubscribes.forEach((u) => u());
    this.unsubscribes = [];
    const codeToLeave = this.code;
    if (codeToLeave) {
      invoke('UnsubscribeRoom', codeToLeave).catch(() => {
        // connection may already be closed
      });
    }
  }

  // Fallback: если RoundEnded/MatchEnded не дошли (гонка/потерянный пуш SignalR),
  // ресинкаемся с хабом вскоре после истечения локального таймера раунда.
  private startRoundEndWatchdog(code: string): void {
    this.stopRoundEndWatchdog();
    this.roundEndWatchdog = setInterval(async () => {
      if (this.room?.status !== 'InGame') return;
      const endsAtUtc = this.root.round.endsAtUtc;
      if (!endsAtUtc) return;
      const overdueMs =
        Date.now() + this.root.round.serverClockOffsetMs - new Date(endsAtUtc).getTime();
      if (overdueMs < 3000) return;
      try {
        const snap = await invoke<RoomSnapshot>('SubscribeRoom', code);
        runInAction(() => {
          if (this.room?.status === 'InGame') this.applySnapshot(snap);
        });
      } catch {
        // non-fatal; retried on next tick
      }
    }, 3000);
  }

  private stopRoundEndWatchdog(): void {
    if (this.roundEndWatchdog !== null) {
      clearInterval(this.roundEndWatchdog);
      this.roundEndWatchdog = null;
    }
  }

  private applySnapshot(snap: RoomSnapshot): void {
    this.room = snap.room;
    if (snap.round) this.root.round.applyServerRound(snap.round);
    if (snap.match) this.root.match.applyMatchInfo(snap.match);
    if (snap.results) this.root.match.applyResults(snap.results);
  }

  private upsertPlayer(player: PlayerDto): void {
    if (!this.room) return;
    const idx = this.room.players.findIndex((p) => p.playerId === player.playerId);
    if (idx >= 0) this.room.players[idx] = player;
    else this.room.players.push(player);
  }

  private removePlayer(playerId: string): void {
    if (!this.room) return;
    this.room.players = this.room.players.filter((p) => p.playerId !== playerId);
  }

  private setPlayerReady(playerId: string, isReady: boolean): void {
    const p = this.room?.players.find((pl) => pl.playerId === playerId);
    if (p) p.isReady = isReady;
  }

  private setPlayerConnected(playerId: string, isConnected: boolean): void {
    const p = this.room?.players.find((pl) => pl.playerId === playerId);
    if (p) p.isConnected = isConnected;
  }

  clear(): void {
    this.room = null;
    this.error = null;
    this.isLoading = false;
  }
}
